import type { ReactElement } from "react";
import { Link } from "react-router-dom";

import maternityHeroImage from "../../assets/images/Hero/maternity-photography-guides-hero.webp";
import maternityFamilyHomeImage from "../../assets/images/maternity-family-at-home-portrait.webp";
import maternitySunflowerFieldImage from "../../assets/images/maternity-sunflower-field-portrait.webp";
import maternityPhoto from "../../assets/images/maternity-couple-purple-gown-studio-portrait.webp";
import { UiIcon } from "./components/UiIcons";
import { findResourceArticleByHref, formatResourcePublishedAt } from "./data/resourceArticles";
import { ResourceHubSeo } from "./ResourceSeo";
import "./NewbornPages.scss";
import "./MaternityPages.scss";

const maternityGuideMeta = (href: string): ReactElement | null => {
  const article = findResourceArticleByHref(href);
  if (!article) return null;

  return (
    <time className="maternity-guide-meta" dateTime={article.publishedAt}>
      {formatResourcePublishedAt(article.publishedAt)} · {article.readTime}
    </time>
  );
};

export const MaternityHub = (): ReactElement => (
  <main className="newborn-page maternity-page">
    <ResourceHubSeo category="maternity" />
    <section className="newborn-hub-hero maternity-hub-hero">
      <img
        alt="Expectant mother in a flowing dress during a maternity portrait session"
        className="newborn-hub-hero__background"
        src={maternityHeroImage}
      />

      <div className="newborn-shell newborn-hub-hero__content">
        <nav aria-label="Breadcrumb" className="newborn-breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/resources/">Resources</Link>
          <span>/</span>
          <span>Maternity</span>
        </nav>

        <p className="maternity-hub-hero__eyebrow">MATERNITY PHOTOGRAPHY GUIDES</p>
        <h1>Maternity Photography</h1>
        <p>
          Thoughtful guidance for choosing the right time, feeling prepared and celebrating this season beautifully.
        </p>
      </div>
    </section>

    <section className="newborn-guides-section newborn-shell maternity-guides-section">
      <div className="maternity-guides-section__heading">
        <div>
          <p>START HERE</p>
          <h2>Plan your maternity session</h2>
        </div>
        <span>Simple, reassuring answers for every stage of pregnancy.</span>
      </div>

      <Link
        className="maternity-guide-feature"
        to="/resources/maternity/when-is-the-best-time-to-take-maternity-photos/"
      >
        <div className="maternity-guide-feature__image">
          <img
            alt="Expectant mother posing in a sunflower field during a maternity session"
            src={maternitySunflowerFieldImage}
          />
          <span>28–34 weeks</span>
        </div>
        <div className="maternity-guide-feature__body">
          <p>SESSION TIMING</p>
          <h2>When Is the Best Time to Take Maternity Photos?</h2>
          <span>
            Find your ideal window based on your due date, comfort and whether you are expecting one baby or multiples.
          </span>
          {maternityGuideMeta("/resources/maternity/when-is-the-best-time-to-take-maternity-photos/")}
          <span className="maternity-guide-feature__link-label">
            Read the guide <UiIcon name="arrow" />
          </span>
        </div>
      </Link>

      <div className="maternity-guide-grid">
        <Link className="maternity-guide-card" to="/resources/maternity/what-to-wear-for-maternity-photos/">
          <span className="maternity-guide-card__visual maternity-guide-card__visual--photo">
            <img
              alt="Pregnant woman in a floral dress posing in a sunflower field"
              loading="lazy"
              src={maternityPhoto}
            />
          </span>
          <span className="maternity-guide-card__body">
            <small>STYLING GUIDE</small>
            <strong>What to Wear for Maternity Photos</strong>
            <span>Soft colors, flattering shapes and easy ways to coordinate your family.</span>
            {maternityGuideMeta("/resources/maternity/what-to-wear-for-maternity-photos/")}
            <b>
              Read the guide <UiIcon name="arrow" />
            </b>
          </span>
        </Link>

        <Link
          className="maternity-guide-card"
          to="/resources/maternity/how-to-prepare-for-your-maternity-photo-session/"
        >
          <span className="maternity-guide-card__visual maternity-guide-card__visual--photo">
            <img
              alt="Expectant mother with her partner and daughter during a maternity session at home"
              loading="lazy"
              src={maternityFamilyHomeImage}
            />
          </span>
          <span className="maternity-guide-card__body">
            <small>SESSION PREP</small>
            <strong>How to Prepare for Your Maternity Photo Session</strong>
            <span>A simple plan for the week before, the day before and photo day.</span>
            {maternityGuideMeta("/resources/maternity/how-to-prepare-for-your-maternity-photo-session/")}
            <b>
              Read the guide <UiIcon name="arrow" />
            </b>
          </span>
        </Link>
      </div>

      <section className="newborn-cta maternity-cta">
        <div className="newborn-cta__icon">
          <UiIcon name="calendar" />
        </div>
        <div>
          <h2>Ready to plan your maternity session?</h2>
          <p>Let&apos;s choose a date that feels comfortable and true to you.</p>
        </div>
        <Link className="newborn-button newborn-button--coral" to="/contact/">
          Ask about availability <UiIcon name="arrow" />
        </Link>
      </section>
    </section>
  </main>
);

export default MaternityHub;
